import { useState, useEffect, useCallback, useRef } from 'react';
import { RefreshCw, Download } from 'lucide-react';
import { isPWAInstalled } from '@/lib/pwa';

// Current app version - should match backend APP_VERSION
const CLIENT_VERSION = '2.1.0';
const VERSION_CHECK_INTERVAL = 5 * 60 * 1000; // Check every 5 minutes
const VERSION_STORAGE_KEY = 'expresssub_last_version';

export function useServiceWorkerUpdate() {
  const [showReload, setShowReload] = useState(false);
  const [waitingWorker, setWaitingWorker] = useState(null);

  const reloadPage = useCallback(() => {
    if (waitingWorker) {
      waitingWorker.postMessage({ type: 'SKIP_WAITING' });
    }
    // Clear service worker caches
    if ('caches' in window) {
      caches.keys().then((names) => {
        names.forEach(name => caches.delete(name));
      });
    }
    localStorage.removeItem(VERSION_STORAGE_KEY);
    const url = new URL(window.location.href);
    url.searchParams.set('v', Date.now());
    window.location.href = url.toString();
  }, [waitingWorker]);

  // Check server version
  const checkServerVersion = useCallback(async () => {
    try {
      const apiUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await fetch(`${apiUrl}/api/version`, {
        cache: 'no-store',
        headers: { 'Cache-Control': 'no-cache', 'Pragma': 'no-cache' }
      });
      
      if (response.ok) {
        const data = await response.json();
        const serverVer = data.version;
        const storedVersion = localStorage.getItem(VERSION_STORAGE_KEY);
        
        if (storedVersion && storedVersion !== serverVer) {
          setShowReload(true);
        } else if (!storedVersion) {
          localStorage.setItem(VERSION_STORAGE_KEY, serverVer);
        }
        
        if (CLIENT_VERSION !== serverVer && storedVersion === serverVer) {
          setShowReload(true);
        }
      }
    } catch (_e) {
      // Silent fail
    }
  }, []);

  useEffect(() => {
    checkServerVersion();
    const versionInterval = setInterval(checkServerVersion, VERSION_CHECK_INTERVAL);
    
    if ('serviceWorker' in navigator) {
      const handleServiceWorkerUpdate = (registration) => {
        if (registration.waiting) {
          setWaitingWorker(registration.waiting);
          setShowReload(true);
        }
      };

      navigator.serviceWorker.ready.then((registration) => {
        if (registration.waiting) {
          handleServiceWorkerUpdate(registration);
        }
        registration.addEventListener('updatefound', () => {
          const newWorker = registration.installing;
          if (newWorker) {
            newWorker.addEventListener('statechange', () => {
              if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                handleServiceWorkerUpdate(registration);
              }
            });
          }
        });
      });

      let refreshing = false;
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        if (!refreshing) {
          refreshing = true;
          window.location.reload();
        }
      });

      const swInterval = setInterval(() => {
        navigator.serviceWorker.ready.then((registration) => {
          registration.update();
        });
      }, VERSION_CHECK_INTERVAL);

      return () => {
        clearInterval(versionInterval);
        clearInterval(swInterval);
      };
    }

    return () => clearInterval(versionInterval);
  }, [checkServerVersion]);

  return { showReload, reloadPage };
}

export default function UpdatePrompt() {
  const { showReload, reloadPage } = useServiceWorkerUpdate();
  const [updating, setUpdating] = useState(false);

  const handleUpdate = () => {
    setUpdating(true);
    setTimeout(() => {
      reloadPage();
    }, 300);
  };

  // Only show update banner on devices that have the app installed as PWA
  if (!isPWAInstalled()) return null;

  if (!showReload || updating) {
    if (updating) {
      return (
        <div
          className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 bg-white border border-emerald-200 shadow-lg rounded-lg px-4 py-3 flex items-center gap-3"
          data-testid="update-banner-updating"
        >
          <RefreshCw className="h-5 w-5 text-emerald-600 animate-spin flex-shrink-0" />
          <span className="text-sm font-medium text-slate-700">Updating...</span>
        </div>
      );
    }
    return null;
  }

  return (
    <div
      className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 bg-white border border-emerald-200 shadow-lg rounded-lg px-4 py-3 flex items-center gap-3 cursor-pointer active:scale-95 transition-transform max-w-[90vw]"
      onClick={handleUpdate}
      role="button"
      tabIndex={0}
      data-testid="update-banner"
    >
      <div className="bg-emerald-100 p-2 rounded-full flex-shrink-0">
        <Download className="h-5 w-5 text-emerald-600" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-medium text-slate-900 text-sm">Update Available</p>
        <p className="text-xs text-slate-500">Tap to update</p>
      </div>
      <RefreshCw className="h-4 w-4 text-emerald-600 flex-shrink-0" />
    </div>
  );
}
